import { TypographyH1, TypographyH4, TypographyMuted, TypographyXS } from "@/components/ui/typography"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover" 
import { useUserQuery } from "@/queries/useUserQuery"
import VirtualizedCommand from "@/components/VirtualizedCommand"
import { useEffect, useMemo, useState } from "react"
import Button from "@/components/Button"
import { User } from "@/types/User"
import { Resale } from "@/types/Resale"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { getInitials } from "@/utils/stringFormatter"
import { useResaleQuery } from "@/queries/useResaleQuery"
import { Input } from "@/components/ui/input" 
import { useUserPermissionsQuery } from "@/queries/useUserPermissionsQuery"
import CardResale from "./components/CardResale"
import { ShieldUser, Search, Building2, Loader2 } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"

const UserPermissions = () => {
    const [selectUser, setSelectUser] = useState<number>(0)
    const [user, setUser] = useState<User>()
    const [enableFetchUserPermissions, setEnableFetchUserPermissions] = useState<boolean>(false)
    const [filterName, setFilterName] = useState<string>('')
    const [filterUf, setFilterUf] = useState<string>('')
    const [filterResaleId, setFilterResaleId] = useState<number | 0>(0)
    const [listResale, setListResale] = useState<Resale[]>([])

    const { data: dataUser, isLoading: isLoadingUsers } = useUserQuery()
    const { 
        data: dataUserPermissions, 
        isLoading: isLoadingUserPermissions, 
        isFetching: isFetchingUserPermissions,
        refetch: refetchUserPermissions 
    } = useUserPermissionsQuery(selectUser, enableFetchUserPermissions)
    const { data: dataResale, isLoading: isLoadingResale } = useResaleQuery()

    const dataResaleFiltered = useMemo(() => {
        return (dataResale || []).filter(item => Boolean(item.CODIGOS_OPERACIONAIS && item.CODIGOS_OPERACIONAIS.trim()))
    }, [dataResale])

    useEffect(() => {
        if (selectUser && dataUser) {
            setUser(dataUser.find(item => item.SK_USUARIO === selectUser))
            setEnableFetchUserPermissions(true)
            refetchUserPermissions()
        } 
    }, [selectUser, dataUser, refetchUserPermissions])

    useEffect(() => {
        if (dataResaleFiltered) {
            let filtered = dataResaleFiltered
            if (filterResaleId > 0) {
                filtered = filtered.filter(item => item.SK_REVENDA === filterResaleId)
            }
            if (filterName) {
                filtered = filtered.filter(item => 
                    item.DS_REVENDA.toLowerCase().includes(filterName.toLowerCase()) ||
                    (item.CODIGOS_OPERACIONAIS && item.CODIGOS_OPERACIONAIS.toLowerCase().includes(filterName.toLowerCase())) ||
                    (item.DS_PROPRIETARIO && item.DS_PROPRIETARIO.toLowerCase().includes(filterName.toLowerCase()))
                )
            }
            if (filterUf) {
                filtered = filtered.filter(item => item.DD_UF === filterUf)
            }
            setListResale(filtered)
        }
    }, [filterName, filterUf, filterResaleId, dataResaleFiltered])

    const listUfs = [...new Set(dataResaleFiltered.map(r => r.DD_UF).filter(Boolean) as string[])]
    const isGridLoading = isLoadingResale || isLoadingUserPermissions || isFetchingUserPermissions

    return (
        <div className="w-full min-h-full p-4 overflow-hidden bg-[#5a8db50d]">
            <div className="flex flex-col gap-4 justify-between mb-4 sm:items-center sm:flex-row">
                <div className="flex gap-3 items-center">
                    <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
                        <ShieldUser className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <TypographyH1>
                            Permissões de Acesso por Revenda
                        </TypographyH1>
                        <TypographyMuted>
                            Gerencie o acesso dos usuários à dimensão lógica de revendas e seus códigos operacionais vinculados
                        </TypographyMuted>
                    </div>
                </div>
            </div>
             
            <div className="border rounded-lg p-4 mt-6 bg-white shadow-sm">
                <TypographyH4 className="mb-4">
                    Seleção de Usuário
                </TypographyH4>
                <div className="grid md:grid-cols-2 md:gap-14 gap-2">
                    <div className="md:max-w-[350px]">
                        <TypographyXS className="flex-1 text-start text-sm mb-2">
                            Selecione o usuário para gerenciar permissões:
                        </TypographyXS>
                        {isLoadingUsers ? (
                            <Skeleton className="h-10 w-full rounded-md" />
                        ) : dataUser ? (
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="justify-start w-full gap-2 text-xs"
                                    >
                                        {selectUser === 0 ? (
                                            <TypographyXS className="flex-1 text-start">
                                                Selecione um usuário
                                            </TypographyXS>
                                        ) : (
                                            <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                {dataUser.find(supp => supp.SK_USUARIO === selectUser)?.NM_USUARIO}
                                            </TypographyXS>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="z-50 p-0 h-60 w-96" align="start">
                                    <VirtualizedCommand
                                        options={dataUser.map(item => ({ value: item.SK_USUARIO.toString(), label: item?.NM_USUARIO }))}
                                        placeholder="Usuário" 
                                        selectedValues={{ [`${selectUser}`]: true }}
                                        onSelectionChange={(data) => {
                                            setSelectUser(Number(data)) 
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                        ) : null}
                    </div>

                    {user && (
                        <div className="mt-2 bg-[#5a8db50d] p-4 rounded-lg border max-w-[500px]">   
                            <div className="flex items-center gap-3">
                                <Avatar className="w-10 h-10 text-xs">
                                    <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-base">
                                        {getInitials(user.NM_USUARIO || '')}
                                    </AvatarFallback>  
                                </Avatar>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <TypographyXS className="text-start text-sm font-semibold mb-0.5">
                                            {user.NM_USUARIO}
                                        </TypographyXS>
                                        {isFetchingUserPermissions && (
                                            <span className="flex items-center gap-1 text-[11px] text-primary font-medium">
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando...
                                            </span>
                                        )}
                                    </div>
                                    <TypographyMuted className="text-xs">
                                        {user.DD_EMAIL || 'Sem e-mail cadastrado'} • Perfil: <strong className="text-gray-700">{user.DD_PERFIL}</strong>
                                    </TypographyMuted>
                                </div>
                            </div>
                        </div>
                    )} 
                </div>

                {!user && (
                    <div className="mt-4 bg-[#5a8db50d] p-4 rounded-xl border max-w-[550px]">   
                        <TypographyXS className="flex-1 text-start text-sm font-semibold mb-2 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-primary" /> Instruções do Controle por Revenda Lógica
                        </TypographyXS>
                        <ul className="list-disc px-5 space-y-1">
                            <li className="text-sm text-gray-600">Selecione um usuário no filtro acima.</li>
                            <li className="text-sm text-gray-600">Conceda ou remova acesso à <strong>Revenda Lógica</strong>.</li>
                            <li className="text-sm text-gray-600">Todos os códigos operacionais associados à revenda serão resolvidos automaticamente.</li>
                            <li className="text-sm text-gray-600">Novos códigos adicionados à revenda herdarão o acesso do usuário sem necessidade de reconfiguração.</li>
                        </ul>  
                    </div>
                )}
            </div>

            {user && (
                <div className="border rounded-lg p-4 mt-4 bg-white shadow-sm">
                    <div className="grid gap-3 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 mb-4">
                        <div>
                            <TypographyXS className="flex-1 text-start text-sm mb-2">Buscar por Nome, Código Operacional ou Proprietário</TypographyXS> 
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                                <Input  
                                    placeholder="Filtre revendas..."
                                    className="pl-9 text-xs"
                                    onChange={(e) => setFilterName(e.target.value)}
                                /> 
                            </div>
                        </div>
                        <div>
                            <TypographyXS className="flex-1 text-start text-sm mb-2">Selecionar / Filtrar por Revenda</TypographyXS>
                            {isLoadingResale ? (
                                <Skeleton className="h-10 w-full rounded-md" />
                            ) : (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="justify-start w-full gap-2 text-xs"
                                        >
                                            {filterResaleId === 0 ? (
                                                <TypographyXS className="flex-1 text-start">
                                                    Todas as Revendas
                                                </TypographyXS>
                                            ) : (
                                                <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                    {dataResaleFiltered?.find(r => r.SK_REVENDA === filterResaleId)?.DS_REVENDA}
                                                </TypographyXS>
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="z-50 p-0 h-60 w-80" align="start">
                                        <VirtualizedCommand
                                            options={(dataResaleFiltered || []).map(r => ({ value: r.SK_REVENDA.toString(), label: r.DS_REVENDA }))}
                                            placeholder="Revenda" 
                                            selectedValues={{ [`${filterResaleId}`]: true }}
                                            onClearFilters={() => setFilterResaleId(0)}
                                            onSelectionChange={(data) => setFilterResaleId(Number(data))}
                                        />
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                        <div>
                            <TypographyXS className="flex-1 text-start text-sm mb-2">Filtrar por UF</TypographyXS>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="justify-start w-full gap-2 text-xs"
                                    >
                                        {filterUf === '' ? (
                                            <TypographyXS className="flex-1 text-start">
                                                Todas as UFs
                                            </TypographyXS>
                                        ) : (
                                            <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                UF: {filterUf}
                                            </TypographyXS>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="z-50 p-0 w-64" align="start">
                                    <VirtualizedCommand
                                        options={listUfs.map(uf => ({ value: uf, label: `Estado ${uf}` }))}
                                        placeholder="UF" 
                                        selectedValues={{ [`${filterUf}`]: true }}
                                        onClearFilters={() => setFilterUf('')}
                                        onSelectionChange={(data) => setFilterUf(data)}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    {isGridLoading ? (
                        <div className="grid gap-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 p-1">
                            {Array.from({ length: 6 }).map((_, idx) => (
                                <div key={idx} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between gap-3 h-44">
                                    <div className="space-y-2">
                                        <Skeleton className="h-5 w-3/4" />
                                        <Skeleton className="h-4 w-1/2" />
                                        <Skeleton className="h-4 w-full" />
                                    </div>
                                    <div className="flex justify-between items-center pt-3 border-t">
                                        <Skeleton className="h-6 w-24 rounded-full" />
                                        <Skeleton className="h-8 w-28 rounded-md" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : listResale && listResale.length > 0 ? (
                        <div className="max-h-[70vh] grid gap-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 overflow-auto p-1">
                            {listResale.map((resaleItem) => (
                                <CardResale 
                                    key={resaleItem.SK_REVENDA} 
                                    dataUserPermissions={dataUserPermissions || []} 
                                    resale={resaleItem} 
                                    idUser={selectUser}
                                    userProfile={user?.DD_PERFIL}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="py-8 text-center text-gray-500">
                            Nenhuma revenda encontrada com os filtros aplicados.
                        </div>
                    )} 
                </div>
            )}
        </div>
    )
}

export default UserPermissions

